'use client'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
  Dispatch,
  SetStateAction,
} from 'react'
import _ from 'lodash'
import {getCookie} from 'cookies-next'
// Utils
import {
  getVoterById,
  getVotesByBallotIdAndVoterId,
} from '../lib/api'
import {logDataByLevel} from '../utils/logging-helpers'
import {
  getStoredVoterId,
  hasActiveVoterScope,
  isVoterStorageEventKey,
  setStoredVoterId,
} from '../utils/voter-storage'
// Consts
import {INPUT_DEBOUNCE_MS} from '../consts/timing'
// Types
import {Voter, Vote} from '../types/voter'

interface VoterContextValue {
  voter: Voter | null
  setVoter: Dispatch<SetStateAction<Voter | null>>
  isLoadingVoter: boolean
  clearVoter: () => void
}

interface VoterProviderProps {
  ballotId?: string
  customerId?: string
  children: ReactNode
}

const VoterContext = createContext<VoterContextValue | undefined>(undefined)

export const VoterProvider = ({
  ballotId,
  customerId,
  children,
}: VoterProviderProps) => {
  const isLoadingVoterRef = useRef<boolean>(false)
  const [voter, setVoter] = useState<Voter | null>(null)
  const [isLoadingVoter, setIsLoadingVoter] = useState<boolean>(true)
  const voterStorageScope = useMemo(() => ({
    ballotId,
    customerId,
  }), [ballotId, customerId])
  // const logVoterSyncDebug = useCallback((trigger: string, data: Record<string, unknown> = {}) => {
  //   if (process.env.NEXT_PUBLIC_VERCEL_ENV !== 'development') return

  //   logDataByLevel({
  //     level: 'info',
  //     message: `Voter sync debug: ${trigger}`,
  //     data: {
  //       ballotId,
  //       customerId,
  //       ...data,
  //     },
  //   })
  // }, [ballotId, customerId])

  const handleAsyncError = (error: any, message: string): null => {
    setIsLoadingVoter(false)
    logDataByLevel({
      level: 'error',
      message: message,
      data: error,
    })
    return null
  }

  const loadVoter = useCallback(async (storedVoterId: string) => {
    if (isLoadingVoterRef.current) return
    isLoadingVoterRef.current = true
    try {
      const voterData = await getVoterById(storedVoterId).catch((error: any) => handleAsyncError(error, 'Error getting voter by ID'))
      if (!voterData) throw new Error('No loaded voter data')
      setStoredVoterId(voterData._id, voterStorageScope)

      const newVoterVotes = await getVotesByBallotIdAndVoterId(ballotId!, storedVoterId).catch((error: any) => handleAsyncError(error, 'Error getting votes by ballot ID and voter ID'))
      voterData.votes = newVoterVotes?.filter((vote: Vote) => !vote.is_submission) || []

      voterData.submissions = newVoterVotes?.filter((vote: Vote) => vote.is_submission) || []

      const hasRegisteredForTextBallot = (
        _.findIndex(voterData?.email_opt_ins_subscribed, optIn => {
          return optIn.ballot_id === ballotId
        }) > -1
      )
      setVoter(hasRegisteredForTextBallot ? voterData : null)
    } catch (error) {
      logDataByLevel({
        level: 'error',
        message: error instanceof Error ? error.message : 'Unknown error',
        data: error,
      })
    } finally {
      isLoadingVoterRef.current = false
    }
  }, [
    ballotId,
    customerId,
    voterStorageScope,
  ])

  const debouncedLoadVoter = useMemo(() => _.debounce(async (storedVoterId: string) => {
    setIsLoadingVoter(true)
    await loadVoter(storedVoterId)
    setIsLoadingVoter(false)
  }, INPUT_DEBOUNCE_MS), [loadVoter])

  const loadVoterData = useCallback(async () => {
    if (isLoadingVoterRef.current || !voter) return
    setStoredVoterId(voter._id, voterStorageScope)

    isLoadingVoterRef.current = true
    try {
      let updatedVoter = _.cloneDeep(voter)

      if (!updatedVoter.votes || !updatedVoter.submissions) {
        const newVoterVotes = await getVotesByBallotIdAndVoterId(ballotId!, updatedVoter._id).catch((error: any) => handleAsyncError(error, 'Error getting votes by ballot ID and voter ID'))
        updatedVoter.votes = newVoterVotes?.filter((vote: Vote) => !vote.is_submission) || []
        updatedVoter.submissions = newVoterVotes?.filter((vote: Vote) => vote.is_submission) || []
      }

      setVoter(updatedVoter)
    } catch (error) {
      logDataByLevel({
        level: 'error',
        message: error instanceof Error ? error.message : 'Unknown error',
        data: error,
      })
    } finally {
      isLoadingVoterRef.current = false
    }
  }, [
    ballotId,
    customerId,
    voter,
    voterStorageScope,
  ])

  const debouncedLoadVoterData = useMemo(() => _.debounce(async () => {
    setIsLoadingVoter(true)
    await loadVoterData()
    setIsLoadingVoter(false)
  }, INPUT_DEBOUNCE_MS), [loadVoterData])

  const syncVoterFromAuthSources = useCallback(() => {
    if (!ballotId) {
      logDataByLevel({
        level: 'warn',
        message: 'No ballot ID provided to VoterProvider',
        data: null,
      })
      return
    }

    const scopedVoterId = getStoredVoterId(voterStorageScope)
    const voterIdCookie = getCookie('voter_id')
    const currentScopeIsActive = hasActiveVoterScope(voterStorageScope)
    const resolvedVoterId = scopedVoterId || (currentScopeIsActive ? voterIdCookie : null)

    if (!resolvedVoterId) {
      if (voter) setVoter(null)
      setIsLoadingVoter(false)
      return
    }

    if (!voter || voter._id !== resolvedVoterId) {
      debouncedLoadVoter(String(resolvedVoterId))
      return
    }

    if (!voter.votes) {
      debouncedLoadVoterData()
      return
    }

    setIsLoadingVoter(false)
  }, [
    ballotId,
    voterStorageScope,
    voter,
    debouncedLoadVoter,
    debouncedLoadVoterData,
  ])

  useEffect(() => {
    syncVoterFromAuthSources()

    return () => {
      debouncedLoadVoter.cancel()
      debouncedLoadVoterData.cancel()
    }
  }, [
    syncVoterFromAuthSources,
    debouncedLoadVoter,
    debouncedLoadVoterData,
  ])

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (!isVoterStorageEventKey(event.key)) return
      syncVoterFromAuthSources()
    }

    const handleWindowFocus = () => {
      syncVoterFromAuthSources()
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      syncVoterFromAuthSources()
    }

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('focus', handleWindowFocus)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('focus', handleWindowFocus)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [syncVoterFromAuthSources])

  const clearVoter = useCallback(() => setVoter(null), [])

  const contextValue = useMemo(() => {
    return {
      voter,
      setVoter,
      isLoadingVoter,
      clearVoter,
    }
  }, [
    voter, isLoadingVoter, clearVoter,
  ])

  return (
    <VoterContext.Provider value={contextValue}>
      {children}
    </VoterContext.Provider>
  )
}

export const useVoterContext = (): VoterContextValue => {
  const context = useContext(VoterContext)
  if (!context) {
    throw new Error('useVoterContext must be used within a VoterProvider')
  }
  return context
}
